// Reading and changing file requests, for the Legal Aid Status Monitor and the Aid Planner
// alike, so both work on the same records. Only statuses and notes are sent; letters stay where they
// were uploaded.

import { base44 } from "@/api/base44Client";
import { emailRequestUpdate, manualRequestDraft, manualRequestPayload, normaliseFileRequests } from "@/lib/fileRequests";
import { deviceStorage } from "@/lib/deviceStorage";
import { isLacwLocalTestMode } from "@/lib/lacwLocalTestStore";
import { readDemoFileRequests, writeDemoFileRequests } from "@/lib/remindersDemo";

// LACW preview test mode has no portal session and no email feed, so file requests are kept
// in this browser (the reminders demo loads some). Nothing leaves the browser.
const testMode = () => isLacwLocalTestMode();

function updateTestRequest(request, changes) {
  const data = readDemoFileRequests(deviceStorage);
  if (request.isManual) {
    data.drafts = data.drafts.map((draft) => (draft.id === request.draftId ? { ...draft, form_data: { ...draft.form_data, ...changes } } : draft));
  } else {
    data.emails = data.emails.map((email) => (email.message_id === request.messageId ? { ...email, ...changes } : email));
  }
  writeDemoFileRequests(deviceStorage, data);
}

async function json(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.ok === false) throw new Error(body?.error || "The file request list could not be reached.");
  return body;
}

export async function fetchFileRequests(brand = "lacw") {
  if (testMode()) {
    const data = readDemoFileRequests(deviceStorage);
    return { requests: normaliseFileRequests(data.emails, data.drafts), emailFeedMissing: false };
  }
  const [emailsResult, draftsResult] = await Promise.allSettled([
    fetch(`/api/lacw-emails?client_id=${encodeURIComponent(brand)}&limit=500`, { credentials: "same-origin" }).then(json),
    fetch(`/api/vals-drafts?brand=${encodeURIComponent(brand)}&form_type=manual_email_request`, { credentials: "same-origin" }).then(json),
  ]);
  if (emailsResult.status === "rejected" && draftsResult.status === "rejected") throw emailsResult.reason;
  const emails = emailsResult.status === "fulfilled" ? emailsResult.value.emails || [] : [];
  const drafts = draftsResult.status === "fulfilled" && Array.isArray(draftsResult.value) ? draftsResult.value : [];
  return {
    requests: normaliseFileRequests(emails, drafts),
    emailFeedMissing: emailsResult.status === "rejected",
  };
}

async function saveManual(request, formData) {
  await json(await fetch("/api/vals-drafts", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: request.draftId,
      form_type: "manual_email_request",
      draft_name: request.draftName || `File request - ${request.clientName}`,
      form_data: { ...request.raw.form_data, ...formData },
    }),
  }));
}

// The Render service saves a request's status and notes together: whichever is left out is
// cleared. So both are always sent - changing the status used to wipe the notes (and the
// letters attached in them), and saving a note reset the status to "New file request".
//
// A request is a whole email thread (normaliseFileRequests), so the change goes to every
// email in it - otherwise a later email's older status would show through again.
async function saveEmailRequest(request, changes) {
  const body = JSON.stringify(emailRequestUpdate(request, changes));
  const ids = request.threadMessageIds?.length ? request.threadMessageIds : [request.messageId];
  await Promise.all(ids.map(async (id) => json(await fetch(`/api/lacw-emails/${encodeURIComponent(id)}`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body,
  }))));
}

export async function saveRequestStatus(request, status) {
  if (testMode()) return updateTestRequest(request, { status });
  // A manual request is stamped here, as Deadline Guard stamps an emailed one.
  if (request.isManual) {
    return saveManual(request, status === request.status ? { status } : { status, statusUpdatedAt: new Date().toISOString() });
  }
  await saveEmailRequest(request, { status });
}

export async function saveRequestNotes(request, notes) {
  if (testMode()) return updateTestRequest(request, { notes });
  if (request.isManual) return saveManual(request, { notes });
  await saveEmailRequest(request, { notes });
}

// A letter attached to a request is stored in the portal's files and linked from its notes.
export async function attachRequestLetter(request, label, file) {
  const { file_url } = await base44.integrations.Core.UploadFile({ file });
  const link = `[${label}: ${file.name}](${file_url})`;
  const notes = request.notes ? `${request.notes}\n\n${link}` : link;
  await saveRequestNotes(request, notes);
  return notes;
}

export async function createManualRequest(details, brand = "lacw") {
  if (testMode()) {
    const data = readDemoFileRequests(deviceStorage);
    const draft = manualRequestDraft(details, brand);
    writeDemoFileRequests(deviceStorage, { ...data, drafts: [...data.drafts, { ...draft, created_at: new Date().toISOString() }] });
    return;
  }
  // The draft's own id is "manual_<timestamp>", which identifies the request inside
  // form_data. It is not a uuid, and vals_drafts.id is, so sending it as the row id made
  // Postgres reject every insert - Add a request failed against the real database while
  // appearing to work in test mode, which writes to this browser instead.
  //
  // The database generates the row id; the manual id stays in form_data.message_id, which
  // is what the rest of the planner matches on.
  await json(await fetch("/api/vals-drafts", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(manualRequestPayload(details, brand)),
  }));
}

/** Saves a request another tool raised (a travel claim's flights), already in record form. */
export async function createRequestRecord(payload) {
  if (testMode()) {
    const data = readDemoFileRequests(deviceStorage);
    writeDemoFileRequests(deviceStorage, { ...data, drafts: [...data.drafts, { ...payload, id: payload.form_data.message_id, created_at: new Date().toISOString() }] });
    return;
  }
  await json(await fetch("/api/vals-drafts", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }));
}

/** Where one of a request email's own attachments opens (firm-checked by the Render service). */
export function requestAttachmentUrl(request, index) {
  return `/api/lacw-emails/${encodeURIComponent(request.messageId)}/attachments/${encodeURIComponent(index)}`;
}

/**
 * The whole request: its full text and the files that came with the email. Requests added by
 * hand have only what was typed. Each part is fetched on its own, so a Gmail problem with the
 * attachments still shows the text.
 */
export async function fetchRequestDetail(request) {
  if (testMode() || request.isManual) {
    const source = request.isManual ? request.raw?.form_data || {} : request.raw || {};
    return { body: source.body || request.snippet || "", from: source.from_address || "", attachments: [], attachmentsError: "" };
  }
  const base = `/api/lacw-emails/${encodeURIComponent(request.messageId)}`;
  const [emailResult, attachmentsResult] = await Promise.allSettled([
    fetch(base, { credentials: "same-origin" }).then(json),
    fetch(`${base}/attachments`, { credentials: "same-origin" }).then(json),
  ]);
  if (emailResult.status === "rejected") throw emailResult.reason;
  const email = emailResult.value.email || {};
  return {
    body: email.body || request.snippet || "",
    from: email.from_address || "",
    attachments: attachmentsResult.status === "fulfilled" ? attachmentsResult.value.attachments || [] : [],
    attachmentsError: attachmentsResult.status === "rejected" ? attachmentsResult.reason?.message || "The attachments couldn't be listed." : "",
  };
}
